import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Slider from "react-slick";
import { Typography, Button, CircularProgress } from "@mui/material";
import { Link } from "react-router-dom";
import SuccessStories from "../components/SuccesStory";
import WhyChoose from "../components/WhyChooseUs";
import AboutSection from "../components/AboutSection";
import RecentEvents from "../components/RecentEvents";
import PremiumPlans from "../components/PlanDetails";
import InfoCards from "../components/InfoCards";
import UpcomingEvent from "../components/UpcomingEvents";
import YouTubeSection from "../components/YouTubeSection";
import EventPopup from "../components/EventPopup";

// Bulletproof fallback data in case fetching public/config/homepageConfig.json fails
const fallbackConfig = {
  hero: {
    slides: [
      {
        img: "https://th.bing.com/th/id/R.c1357742c493bb72966cf8b40360a9f9?rik=7WiCyQYuEAZekg&riu=http%3a%2f%2fwebneel.com%2fdaily%2fsites%2fdefault%2ffiles%2fimages%2fdaily%2f07-2018%2f4-kerala-wedding-photographer-weddingnovels.jpg&ehk=7d0l88s3EfCaP8WS7PHTJdZ9rpkX8rTfMt%2fItF8f4r8%3d&risl=&pid=ImgRaw&r=0",
        title: "Find Your Perfect Life Partner",
        subtitle: "Trusted Matrimonial Services for Happy Marriages"
      },
      {
        img: "https://cdn0.weddingwire.in/article/8088/original/960/jpg/118808-tamil-marriage-dates-2024-happy-theory-april.jpeg",
        title: "Matchmaking Made Simple & Secure",
        subtitle: "Thousands of Verified Profiles Awaiting You"
      },
      {
        img: "https://crystallinestudio.com/imgBanner/81-crystalline-banner.jpg",
        title: "Begin Your Beautiful Journey Today",
        subtitle: "Celebrating Love, Trust, and Lifelong Companionship"
      }
    ]
  },
  usp: {
    title: "Why Choose Vivaha Sangamam?",
    stats: [
      { value: 5000, suffix: "+", label: "Verified Profiles" },
      { value: 500, suffix: "+", label: "Successful Marriages" },
      { value: 98, suffix: "%", label: "Satisfaction Rate" }
    ],
    features: [
      {
        icon: "VerifiedUser",
        title: "100% Verified Profiles",
        desc: "Every single profile is manually verified by our backend team to ensure authentic and genuine matches."
      },
      {
        icon: "ThumbUp",
        title: "High Success Rate",
        desc: "Our platform has one of the highest match-success rates in Tamil Nadu, uniting hearts for years."
      },
      {
        icon: "VolunteerActivism",
        title: "Trusted by Thousands",
        desc: "We have helped thousands of couples find their perfect life partner and start a happy family."
      }
    ]
  },
  successStories: [],
  upcomingEvents: [],
  youtubeSection: {
    channelUrl: "https://www.youtube.com/channel/UCCyJ4JnFddu7WSzDUK6JV3w",
    playlistId: "UUCyJ4JnFddu7WSzDUK6JV3w",
    channelTitle: "Vivaha Sangamam YouTube Channel",
    videos: []
  },
  recentEvents: []
};

// Parses dates like "12 July 2026" (day, month name, year) into a Date at
// local midnight. Returns null if the string doesn't match that shape.
function parseEventDate(dateStr) {
  if (!dateStr) return null;
  const parts = dateStr.trim().split(" ");
  if (parts.length < 3) return null;
  const day = parseInt(parts[0], 10);
  const year = parseInt(parts[2], 10);
  const monthIndex = MONTH_NAMES.findIndex((m) => m === parts[1].slice(0, 3).toLowerCase());
  if (isNaN(day) || isNaN(year) || monthIndex === -1) return null;
  return new Date(year, monthIndex, day);
}

const MONTH_NAMES = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];

// Both the popup and the "Upcoming Events" section should only surface
// events that haven't happened yet. Once an event's date has passed, it
// drops out of both automatically; publishing a new event with a future
// date (via the admin panel) is all that's needed to bring it back.
function getUpcomingEvents(upcomingEvents) {
  if (!upcomingEvents || !upcomingEvents.length) return [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return upcomingEvents.filter((event) => {
    const eventDate = parseEventDate(event.date);
    return eventDate && eventDate >= today;
  });
}

export default function Home() {
  const [config, setConfig] = useState(fallbackConfig);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch("/config/homepageConfig.json")
      .then((res) => {
        if (!res.ok) {
          throw new Error("Failed to fetch configuration");
        }
        return res.json();
      })
      .then((data) => {
        setConfig(data);
        setLoading(false);
      })
      .catch((err) => {
        console.warn("Using fallback configuration due to error:", err);
        setConfig(fallbackConfig);
        setLoading(false);
      });
  }, []);

  const settings = {
    dots: false,
    infinite: true,
    autoplay: true,
    fade: true,
    speed: 1000,
    autoplaySpeed: 3500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: false,
    pauseOnHover: false,
  };

  const slides = config.hero?.slides || fallbackConfig.hero.slides;
  const upcomingEvents = getUpcomingEvents(config.upcomingEvents);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
        <CircularProgress sx={{ color: "#BF0603" }} />
      </Box>
    );
  }

  return (
    <>
      {/* ✅ DESKTOP VIEW (Hero Slider with Typography Overlays) */}
      <Box
        sx={{
          display: { xs: "none", md: "block" },
          position: "relative",
          height: "90vh",
          marginTop: "90px",
          overflow: "hidden",
          width: '100%',
        }}
      >
        <Slider {...settings}>
          {slides.map((s, i) => (
            <Box
              key={i}
              sx={{
                width: "100%",
                height: "90vh",
                backgroundImage: `url(${s.img})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                position: "relative",
              }}
            >
              {/* Dark Gradient Overlay for optimal readability */}
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  background: "linear-gradient(to right, rgba(0,0,0,0.65) 0%, rgba(0,0,0,0.3) 100%)",
                  zIndex: 10,
                }}
              />
              
              {/* Overlay Content */}
              <Box
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "8%",
                  transform: "translateY(-50%)",
                  maxWidth: "700px",
                  zIndex: 20,
                  color: "#fff",
                  display: "flex",
                  flexDirection: "column",
                  gap: 2
                }}
              >
                <Typography
                  variant="h2"
                  sx={{
                    fontWeight: 800,
                    fontFamily: "Quicksand",
                    fontSize: { md: "3.5rem", lg: "4rem" },
                    lineHeight: 1.2,
                    textShadow: "2px 2px 8px rgba(0,0,0,0.5)"
                  }}
                >
                  {s.title}
                </Typography>
                
                <Typography
                  variant="h5"
                  sx={{
                    fontFamily: "Varela Round",
                    fontSize: "1.35rem",
                    fontWeight: 400,
                    color: "rgba(255,255,255,0.9)",
                    textShadow: "1px 1px 4px rgba(0,0,0,0.5)",
                    mb: 2
                  }}
                >
                  {s.subtitle}
                </Typography>

                <Box sx={{ display: "flex", gap: 2 }}>
                  <Button
                    variant="contained"
                    component={Link}
                    to="/contact-us"
                    sx={{
                      backgroundColor: "#BF0603",
                      color: "#fff",
                      fontWeight: 700,
                      fontFamily: "Quicksand",
                      px: 4,
                      py: 1.5,
                      borderRadius: "30px",
                      fontSize: "1rem",
                      boxShadow: "0 4px 15px rgba(191, 6, 3, 0.4)",
                      "&:hover": {
                        backgroundColor: "#9e0402",
                        boxShadow: "0 6px 20px rgba(191, 6, 3, 0.6)"
                      }
                    }}
                  >
                    Find a Match
                  </Button>
                  
                  <Button
                    variant="outlined"
                    component={Link}
                    to="/about-us"
                    sx={{
                      borderColor: "#fff",
                      color: "#fff",
                      fontWeight: 700,
                      fontFamily: "Quicksand",
                      px: 4,
                      py: 1.5,
                      borderRadius: "30px",
                      fontSize: "1rem",
                      borderWidth: "2px",
                      "&:hover": {
                        borderColor: "#D4AF37",
                        color: "#D4AF37",
                        borderWidth: "2px"
                      }
                    }}
                  >
                    Learn More
                  </Button>
                </Box>
              </Box>
            </Box>
          ))}
        </Slider>
      </Box>

      {/* ✅ MOBILE + TABLET VIEW (Hero Slider with Typography Overlays) */}
      <Box
        sx={{
          display: { xs: "block", md: "none" },
          marginTop: "90px",
        }}
      >
        <Box sx={{ height: "60vh", position: "relative" }}>
          <Slider {...settings}>
            {slides.map((s, i) => (
              <Box
                key={i}
                sx={{
                  width: "100%",
                  height: "60vh",
                  backgroundImage: `url(${s.img})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  position: "relative"
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    background: "rgba(0, 0, 0, 0.55)",
                    zIndex: 10,
                  }}
                />
                
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    zIndex: 20,
                    color: "#fff",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    textAlign: "center",
                    px: 3,
                    gap: 1.5
                  }}
                >
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 800,
                      fontFamily: "Quicksand",
                      fontSize: "1.8rem",
                      lineHeight: 1.3
                    }}
                  >
                    {s.title}
                  </Typography>
                  
                  <Typography
                    variant="body1"
                    sx={{
                      fontFamily: "Varela Round",
                      fontSize: "0.95rem",
                      color: "rgba(255,255,255,0.85)",
                      mb: 2
                    }}
                  >
                    {s.subtitle}
                  </Typography>

                  <Button
                    variant="contained"
                    component={Link}
                    to="/contact-us"
                    sx={{
                      backgroundColor: "#BF0603",
                      color: "#fff",
                      fontWeight: 700,
                      fontFamily: "Quicksand",
                      px: 3,
                      py: 1,
                      borderRadius: "20px",
                      fontSize: "0.9rem"
                    }}
                  >
                    Find a Match
                  </Button>
                </Box>
              </Box>
            ))}
          </Slider>
        </Box>
      </Box>

      {/* Flash-screen popup for upcoming event */}
      <EventPopup event={upcomingEvents[0] || null} />

      {/* Sections rendering with dynamic config configurations */}
      <SuccessStories config={config.successStories} />
      <WhyChoose config={config.usp} />
      <UpcomingEvent config={upcomingEvents} />
      <RecentEvents config={config.recentEvents} />
      
      {/* Premium Dynamic YouTube Channel Integration Section */}
      <YouTubeSection config={config.youtubeSection} />
      
      <AboutSection />
      <PremiumPlans />
      <InfoCards />
    </>
  );
}
